import { type CrossClientQueryClientQueryMode, CrossClientQueryClientQueryModes, type CrossClientQueryDataSource } from '../../../../models/CrossClient'
import { formatRelativeDateTime } from '../../../../common/dateUtils'
import { CLIENT_MODE_LABEL, getIntegrationSourceDisplayNames, isZocksSource, joinNames, pluralize } from '../../utils'
import { CcqCards, CcqCriterionStates, type CcqCard, type CcqDecidingState } from './ccqResultView'

export const formatCount = (value: number): string => value.toLocaleString('en-US')

export const CCQ_ZOCKS = 'Zocks'

export const ccqSourceNames = (sources: CrossClientQueryDataSource[]): string[] => [
  ...(sources.some(isZocksSource) ? [CCQ_ZOCKS] : []),
  ...getIntegrationSourceDisplayNames(sources),
]

export const CCQ_INIT_TITLE = 'Setting up your search'

// Number of criteria from which the complex note shows.
export const CCQ_INIT_COMPLEX_FROM = 3

export const CCQ_INIT_COMPLEX_NOTE = 'Complex searches take a little longer.'

export const CCQ_NOTHING_RESOLVABLE = 'Client Profiles in Zocks and connected records are not applicable for this query.'

export const ccqStatusChecking = (sourceNames: string[]): string => `Checking clients in ${joinNames(sourceNames)}`

export const ccqOutOf = (count: number, total: number): string => `${formatCount(count)} of ${formatCount(total)}`

export const ccqStatusDone = (total: number): string => (total === 1 ? 'Checked 1 client' : `Checked all ${formatCount(total)} clients`)

export const ccqStatusStopped = (checked: number, total: number): string =>
  `Stopped after checking ${formatCount(checked)} of ${formatCount(total)} ${pluralize(total, 'client')}`

export const CCQ_CARD_LABEL: Record<CcqCard, string> = {
  [CcqCards.MATCHED]: 'Matched',
  [CcqCards.CLOSER]: 'Needs a closer look',
  [CcqCards.NOT_IN_RECORDS]: 'Not in records',
  [CcqCards.NOT_MATCHED]: 'Not matched',
  [CcqCards.MISSING_DATA]: 'Missing data',
}

export const CCQ_CARD_DESCRIPTION: Record<CcqCard, string> = {
  [CcqCards.MATCHED]: 'These clients meet what you asked for.',
  [CcqCards.CLOSER]: 'Their records cover part of what you asked, but not enough to decide.',
  [CcqCards.NOT_IN_RECORDS]: "Their records don't cover what you asked.",
  [CcqCards.NOT_MATCHED]: "These clients don't meet what you asked for.",
  [CcqCards.MISSING_DATA]: "AI Deep Analysis couldn't find enough in their meetings and notes to decide.",
}

const CCQ_CARD_CLIENTS: Record<CcqCard, { one: string; other: string }> = {
  [CcqCards.MATCHED]: { one: 'matched client', other: 'matched clients' },
  [CcqCards.CLOSER]: { one: 'client that needs a closer look', other: 'clients that need a closer look' },
  [CcqCards.NOT_IN_RECORDS]: { one: 'client not in records', other: 'clients not in records' },
  [CcqCards.NOT_MATCHED]: { one: 'client not matched', other: 'clients not matched' },
  [CcqCards.MISSING_DATA]: { one: 'client with missing data', other: 'clients with missing data' },
}

const ccqCardClients = (card: CcqCard, count: number): string =>
  `${formatCount(count)} ${pluralize(count, CCQ_CARD_CLIENTS[card].one, CCQ_CARD_CLIENTS[card].other)}`

export const ccqSeeAllLabel = (card: CcqCard, count: number): string => `${count === 1 ? 'See' : 'See all'} ${ccqCardClients(card, count)}`

export const CCQ_CLOSE_FULL_LIST = 'Close full list'

// In a moved client's summary column, in place of the criteria that decided their old card.
export const ccqMovedFrom = (card: CcqCard): string => `Moved from ${CCQ_CARD_LABEL[card]}`

export const ccqMovedToast = (name: string, card: CcqCard): string => `${name} moved to ${CCQ_CARD_LABEL[card]}.`

export const ccqMoveFailed = (name: string): string => `Couldn't move ${name}. Try again.`

export const CCQ_VIEW = 'View'

export const CCQ_NO_MATCHES_DONE = 'None of your clients match these criteria.'

export const CCQ_EDIT_AND_RUN_AGAIN = 'Edit and run again'

export const CCQ_CARD_EMPTY_RUNNING = 'No clients here yet.'

export const CCQ_CARD_EMPTY_DONE = 'No clients here.'

export const CCQ_FOOTER_RUNNING = 'Check a few while I finish. Then you can take action on all of them.'

export const ccqFooterDone = (count: number): string =>
  count === 1 ? 'If this looks right, take action on this match.' : `If these look right, take action on all ${formatCount(count)} matches.`

export const ccqFooterSelected = (selected: number, total: number): string => `${formatCount(selected)} of ${formatCount(total)} selected`

export const CCQ_CLEAR_SELECTION = 'Clear selection'

export const CCQ_AGENTS = 'Agents'

export const CCQ_AGENT_PREVIEW_CLIENT = 'Client'

export const CCQ_AGENT_PREVIEW_CLIENTS = 'Clients'

export const CCQ_ACTIONS = 'Actions'

export const CCQ_SEND_EMAIL = 'Send email'

export const CCQ_SEND_BOOKING_INVITATION = 'Send booking invitation'

export const CCQ_SEND_BOOKING_INVITATIONS = 'Send booking invitations'

export const ccqActionCount = (count: number): string => `${formatCount(count)} ${pluralize(count, 'action')}`

export const ccqActionLabel = (count: number, running: number): string =>
  running > 0 ? `${ccqActionCount(count)}, ${formatCount(running)} in progress` : ccqActionCount(count)

export const CCQ_SEARCH_PLACEHOLDER = 'Search by name'

export const CCQ_SEARCH_EMPTY = 'No clients match that name.'

export const CCQ_CRITERION_MET = 'Met:'

export const CCQ_CRITERION_NOT_MET = 'Not met:'

export const CCQ_LIST_LOADING = 'Loading clients…'

export const CCQ_PREVIEWS_FAILED = "Couldn't load these clients."

export const CCQ_TRY_AGAIN = 'Try again'

export const CCQ_ACTION_FILTER_LABEL = 'Filter by actions'

export const CCQ_ACTION_FILTER_ALL = 'All'

export const CCQ_ACTION_FILTER_WITH = 'With actions'

export const CCQ_ACTION_FILTER_WITHOUT = 'Without actions'

export const CCQ_ACTION_FILTER_WITH_EMPTY = 'No actions yet.'

export const CCQ_ACTION_FILTER_WITHOUT_EMPTY = 'Every client here has an action.'

export const CCQ_SELECT_ALL = 'Select all clients on this list'

export const CCQ_FOOTER_ALL_ACTIONED = 'Every match has an action. Take another action, or check the full list.'

export const CCQ_QUERY_DETAILS = 'Details'

export const ccqStarted = (startedAt: string, by: string): string => `${formatRelativeDateTime(new Date(startedAt))}${by ? ` by ${by}` : ''}`

export const CCQ_STARTED_BY_YOU = 'you'

export const ccqClientsLabel = (mode: CrossClientQueryClientQueryMode, total: number): string =>
  `${CLIENT_MODE_LABEL[mode] ?? CLIENT_MODE_LABEL[CrossClientQueryClientQueryModes.WITH_SHARED]} (${formatCount(total)} ${pluralize(total, 'client')})`

export const ccqOwnerLabel = (owner: string): string => `Owner: ${owner}`

export const CCQ_OPEN_PROFILE = 'Profile'

export const ccqNotFoundIn = (sourceNames: string[]): string => `Not found in ${joinNames(sourceNames, 'or')}.`

export const CCQ_NOT_CHECKED = "Not checked. It wouldn't change the result."

export const CCQ_CRITERION_NO_DATA = 'No data yet'

export const CCQ_CRITERIA_SHOW_ALL = 'Show all'

export const CCQ_CRITERIA_HIDE_ALL = 'Hide all'

export const CCQ_SUMMARY_PREFIX: Record<CcqDecidingState, string> = {
  [CcqCriterionStates.MET]: 'Met:',
  [CcqCriterionStates.NOT_MET]: "Didn't meet:",
  [CcqCriterionStates.NOT_FOUND]: 'No data:',
}

// After the named criteria when others decided the card too: ", +2 more".
export const ccqSummaryMore = (count: number): string => `, +${formatCount(count)} more`

export const CCQ_CHIP_LOCKED = "This client hasn't been checked enough to change the match."

export const CCQ_PANEL_CLOSE = 'Close match details'

export const CCQ_PANEL_PREVIOUS = 'Previous client'

export const CCQ_PANEL_NEXT = 'Next client'

export const CCQ_BACK_LABEL = 'Back to your questions'

export const CCQ_AI_RUN = 'Run AI Deep Analysis'

export const CCQ_AI_AWAITING_CLIENT = 'Still checking this client.'

export const CCQ_AI_CHECKING_TAG = 'AI checking'

export const CCQ_AI_FOOTER_RUNNING = "You can run AI Deep Analysis when I'm done."

export const CCQ_AI_FOOTER_DONE = 'Review them yourself, or run AI Deep Analysis on their meetings and notes.'

export const CCQ_AI_FOOTER_DONE_NOT_IN_RECORDS = 'AI Deep Analysis can look for answers in their meetings and notes.'

export const CCQ_AI_STOP = 'Stop'

export const CCQ_AI_STOPPING = 'Stopping…'

export const ccqAiBatchRunning = (count: number): string => `Checking ${formatCount(count)} ${pluralize(count, 'client')}`

interface CcqAiBatchMoves {
  checked: number
  toMatched: number
  toNotMatched: number
  toMissingData: number
}

export const ccqAiBatchResult = ({ checked, toMatched, toNotMatched, toMissingData }: CcqAiBatchMoves): string => {
  const destinations = [
    { count: toMatched, place: 'Matched' },
    { count: toNotMatched, place: 'Not matched' },
    { count: toMissingData, place: 'Missing data' },
  ]
    .filter(({ count }) => count > 0)
    // "10 moved to Matched and 7 to Missing data": only the first says "moved".
    .map(({ count, place }, index) => `${formatCount(count)} ${index === 0 ? 'moved to' : 'to'} ${place}`)
  const moved = destinations.length > 0 ? ` ${joinNames(destinations)}.` : ''
  return `Checked ${formatCount(checked)}.${moved}`
}

// One sentence split around its inline profile link.
export interface CcqGuidanceLine {
  lead?: string
  before: string
  link: string
  after: string
}

export const ccqGuidanceOffer = (card: CcqCard, firstName: string, possessive: string): CcqGuidanceLine => ({
  lead: card === CcqCards.NOT_IN_RECORDS ? `None of what you asked is in ${firstName}'s records.` : `Some of what you asked isn't in ${firstName}'s records.`,
  before: 'Check',
  link: `${possessive} profile`,
  after: `if you know more, or let AI Deep Analysis look through ${possessive} meetings and notes.`,
})

export const ccqGuidanceMissingData = (firstName: string): CcqGuidanceLine => ({
  before: `None of what's missing is in ${firstName}'s records, meetings, or notes. Check`,
  link: 'their profile',
  after: 'if you know more.',
})

export const ccqAiClientRunning = (firstName: string): string => `Looking through ${firstName}'s meetings and notes`
